import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Filter as FilterIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { prioridadeService } from "../services/api";

const Prioridades = () => {
  // ✅ CONFIGURAÇÃO DOS TAMANHOS DOS FILTROS - CSS PURO RESPONSIVO!
  // Valores de 0 a 100 (porcentagem da largura no desktop)
  // Mobile: sempre 100% (empilhados)
  // 💡 TESTE: Mude os valores e veja a diferença imediatamente!
  const TAMANHOS_FILTROS = {
    busca: 100, // 100% - Campo de busca (único campo)
  };

  const { user } = useAuth();

  const [prioridades, setPrioridades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  // Estado para controlar visibilidade dos filtros
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const [filtros, setFiltros] = useState({
    busca: "",
  });
  const [paginacao, setPaginacao] = useState({
    page: 0,
    limit: 10,
    total: 0,
  });

  // Estados para modal de prioridade
  const [modalAberto, setModalAberto] = useState(false);
  const [prioridadeEditando, setPrioridadeEditando] = useState(null);
  const [dadosPrioridade, setDadosPrioridade] = useState({
    nome: "",
    tempo: "",
    cor: "",
  });

  // Estados para notificações
  const [snackbar, setSnackbar] = useState({
    aberto: false,
    mensagem: "",
    tipo: "success",
  });

  // Estados para confirmação de exclusão
  const [confirmacaoExclusao, setConfirmacaoExclusao] = useState({
    aberto: false,
    prioridadeId: null,
    prioridadeNome: "",
  });

  const carregarPrioridades = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: paginacao.page + 1,
        limit: paginacao.limit,
      };

      if (filtros.busca) params.busca = filtros.busca;

      const response = await prioridadeService.getAll(params);
      setPrioridades(response.prioridades || []);
      setPaginacao((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
      }));
    } catch (error) {
      mostrarSnackbar(
        "Erro ao carregar prioridades: " + error.message,
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [paginacao.page, paginacao.limit, filtros]);

  useEffect(() => {
    carregarPrioridades();
  }, [carregarPrioridades]);

  const mostrarSnackbar = (mensagem, tipo = "success") => {
    setSnackbar({
      aberto: true,
      mensagem,
      tipo,
    });
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
    setPaginacao((prev) => ({ ...prev, page: 0 }));
  };

  const abrirModalPrioridade = (prioridade = null) => {
    if (prioridade) {
      setPrioridadeEditando(prioridade);
      setDadosPrioridade({
        nome: prioridade.nome || "",
        tempo: prioridade.tempo || "",
        cor: prioridade.cor || "",
      });
    } else {
      setPrioridadeEditando(null);
      setDadosPrioridade({
        nome: "",
        tempo: "",
        cor: "",
      });
    }
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setPrioridadeEditando(null);
  };

  const handleInputChange = (campo, valor) => {
    setDadosPrioridade((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const salvarPrioridade = async () => {
    if (salvando) return; // Evita cliques duplos

    try {
      setSalvando(true);

      if (!dadosPrioridade.nome.trim()) {
        mostrarSnackbar("Nome é obrigatório", "error");
        return;
      }

      if (!dadosPrioridade.tempo || parseFloat(dadosPrioridade.tempo) <= 0) {
        mostrarSnackbar("Tempo deve ser um número positivo", "error");
        return;
      }

      // Preparar dados para envio
      const dadosParaEnviar = {
        nome: dadosPrioridade.nome.trim(),
        tempo: parseFloat(dadosPrioridade.tempo),
        cor: dadosPrioridade.cor || null,
      };

      if (prioridadeEditando) {
        await prioridadeService.update(prioridadeEditando.id, dadosParaEnviar);
        mostrarSnackbar("Prioridade atualizada com sucesso!");
      } else {
        await prioridadeService.create(dadosParaEnviar);
        mostrarSnackbar("Prioridade criada com sucesso!");
      }

      fecharModal();
      carregarPrioridades();
    } catch (error) {
      mostrarSnackbar("Erro ao salvar prioridade: " + error.message, "error");
    } finally {
      setSalvando(false);
    }
  };

  const confirmarExclusao = (prioridade) => {
    setConfirmacaoExclusao({
      aberto: true,
      prioridadeId: prioridade.id,
      prioridadeNome: prioridade.nome,
    });
  };

  const excluirPrioridade = async () => {
    if (excluindo) return; // Evita cliques duplos

    try {
      setExcluindo(true);
      await prioridadeService.delete(confirmacaoExclusao.prioridadeId);
      mostrarSnackbar("Prioridade excluída com sucesso!");
      carregarPrioridades();
    } catch (error) {
      mostrarSnackbar("Erro ao excluir prioridade: " + error.message, "error");
    } finally {
      setExcluindo(false);
      setConfirmacaoExclusao({
        aberto: false,
        prioridadeId: null,
        prioridadeNome: "",
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPaginacao((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPaginacao((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  // Componente para exibir a cor
  const ExibirCor = ({ cor }) => {
    if (!cor) {
      return (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            border: "2px dashed #ccc",
            backgroundColor: "#f5f5f5",
            display: "inline-block",
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          backgroundColor: cor,
          border: "2px solid #ddd",
          display: "inline-block",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      />
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
          Prioridades
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            sx={{ mr: 2 }}
          >
            Filtros
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => abrirModalPrioridade()}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Nova prioridade
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      {mostrarFiltros && (
        <Card
          sx={{
            mb: 3,
            p: 3,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, color: "#333", fontWeight: 600 }}
          >
            Filtros
          </Typography>

          {/* ✅ SOLUÇÃO CSS PURA - Funciona em qualquer versão do Material-UI */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              flexWrap: { xs: "wrap", md: "nowrap" }, // Mobile: quebra linha, Desktop: mesma linha
              alignItems: "center",
            }}
          >
            {/* Campo de Busca */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.busca}%`,
                minWidth: { xs: "100%", md: "300px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.busca}%` },
              }}
            >
              <TextField
                fullWidth
                label="Buscar por nome"
                placeholder="Ex: Alta, Média, Baixa..."
                value={filtros.busca}
                onChange={(e) => handleFiltroChange("busca", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Botão para limpar filtros */}
          <Box sx={{ mt: 3 }}>
            <Button
              onClick={() => {
                setFiltros({ busca: "" });
                setPaginacao((prev) => ({ ...prev, page: 0 }));
              }}
              variant="outlined"
              size="small"
            >
              Limpar filtros
            </Button>
          </Box>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <TableContainer component={Paper}>
          <Table sx={{ "& .MuiTableCell-root": { py: 1.5 } }}>
            {" "}
            {/* ✅ Reduz altura das linhas */}
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                  Tempo (horas)
                </TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Cor</TableCell>
                {user?.is_super_admin && (
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                    Escritório
                  </TableCell>
                )}
                <TableCell
                  sx={{ fontWeight: 600, textAlign: "center", py: 1.5 }}
                >
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={user?.is_super_admin ? 5 : 4}
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : prioridades.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={user?.is_super_admin ? 5 : 4}
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    Nenhuma prioridade encontrada
                  </TableCell>
                </TableRow>
              ) : (
                prioridades.map((prioridade) => (
                  <TableRow key={prioridade.id} hover>
                    <TableCell sx={{ py: 1.5 }}>{prioridade.nome}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {prioridade.tempo.toString().replace(".", ",")} horas
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <ExibirCor cor={prioridade.cor} />
                    </TableCell>
                    {user?.is_super_admin && (
                      <TableCell sx={{ py: 1.5 }}>
                        {prioridade.escritorio?.nome || "-"}
                      </TableCell>
                    )}
                    <TableCell sx={{ textAlign: "center", py: 1.5 }}>
                      <Tooltip title="Editar">
                        <IconButton
                          onClick={() => abrirModalPrioridade(prioridade)}
                          color="info"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          onClick={() => confirmarExclusao(prioridade)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={paginacao.total}
          page={paginacao.page}
          onPageChange={handleChangePage}
          rowsPerPage={paginacao.limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      </Card>

      {/* Modal de Criação/Edição */}
      <Dialog open={modalAberto} onClose={fecharModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {prioridadeEditando ? "Editar prioridade" : "Nova prioridade"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome *"
            fullWidth
            variant="outlined"
            value={dadosPrioridade.nome}
            onChange={(e) => handleInputChange("nome", e.target.value)}
            placeholder="Ex: Alta, Média, Baixa..."
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Tempo (horas) *"
            type="number"
            fullWidth
            variant="outlined"
            value={dadosPrioridade.tempo}
            onChange={(e) => handleInputChange("tempo", e.target.value)}
            placeholder="Ex: 5, 10, 15..."
            inputProps={{ min: "0.1", step: "0.1" }}
            sx={{ mt: 2 }}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              Cor (opcional)
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <input
                type="color"
                value={dadosPrioridade.cor || "#000000"}
                onChange={(e) => handleInputChange("cor", e.target.value)}
                style={{
                  width: "50px",
                  height: "40px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              />
              <TextField
                label="Código da cor"
                variant="outlined"
                size="small"
                value={dadosPrioridade.cor}
                onChange={(e) => handleInputChange("cor", e.target.value)}
                placeholder="#FF0000"
                sx={{ flex: 1 }}
              />
              {dadosPrioridade.cor && (
                <Button
                  size="small"
                  onClick={() => handleInputChange("cor", "")}
                  variant="outlined"
                >
                  Limpar
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharModal} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={salvarPrioridade}
            variant="contained"
            disabled={salvando}
          >
            {salvando ? (
              <CircularProgress size={20} />
            ) : prioridadeEditando ? (
              "Atualizar"
            ) : (
              "Criar"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={confirmacaoExclusao.aberto}
        onClose={() =>
          setConfirmacaoExclusao({
            aberto: false,
            prioridadeId: null,
            prioridadeNome: "",
          })
        }
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a prioridade "
            {confirmacaoExclusao.prioridadeNome}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmacaoExclusao({
                aberto: false,
                prioridadeId: null,
                prioridadeNome: "",
              })
            }
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={excluirPrioridade}
            color="error"
            variant="contained"
            disabled={excluindo}
          >
            {excluindo ? <CircularProgress size={20} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.aberto}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, aberto: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, aberto: false })}
          severity={snackbar.tipo}
          sx={{ width: "100%" }}
        >
          {snackbar.mensagem}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Prioridades;
